import {showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {useCallback, useEffect, useRef} from 'react';

import {isNil, omit} from 'ramda';
import {isString} from 'ramda-adjunct';

import {
  CreateMinipurchaseApiArg,
  UpdateMinipurchaseApiArg,
  useAddMinipurchaseOfferedVehicleMutation,
  useCreateMinipurchaseMutation,
  useUpdateMinipurchaseMutation,
} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {vehiclesRoutes, testIds} from '@dms/routes';
import {DmsDataGrid, type DmsDataGridProps, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {useInterestData} from '../../../../hooks/useInterestData';
import {EXTERNAL_FILTER_ID} from '../../const/externalFilterId';
import {OFFERABLE_VEHICLES_GRID_CODE} from '../../const/offerableVehiclesGridCode';
import {toTestDriveSlotRequestBody} from '../../utils/toTestDriveSlotRequestBody';
import {transformMinipurchaseToExternalFilter} from '../../utils/transformMinipurchaseToExternalFilter';
import {ExternalFilterConnector, ExternalFilterConnectorRef} from '../ExternalFilterConnector';

interface OfferableVehiclesDatagridProps extends Pick<DmsDataGridProps, 'maximization'> {
  interestId: string;
  externalFilterRef: React.RefObject<ExternalFilterConnectorRef | null>;
}

export function OfferableVehiclesDatagrid(props: OfferableVehiclesDatagridProps) {
  const navigate = useNavigate();
  const [addMinipurchaseOfferedVehicle] = useAddMinipurchaseOfferedVehicleMutation();
  const [updateMinipurchase] = useUpdateMinipurchaseMutation();
  const [createMinipurchase] = useCreateMinipurchaseMutation();
  const {miniPurchase} = useInterestData(props.interestId);

  const miniPurchaseRef = useRef(miniPurchase);

  useEffect(
    function syncMiniPurchaseRef() {
      miniPurchaseRef.current = miniPurchase;
    },
    [miniPurchase]
  );

  const isInitialFilterPushed = useRef(false);

  const handleExternalFilterReady = useCallback(() => {
    if (isInitialFilterPushed.current || !props.externalFilterRef.current) {
      return;
    }
    isInitialFilterPushed.current = true;
    props.externalFilterRef.current.onChange(
      transformMinipurchaseToExternalFilter(miniPurchaseRef.current)
    );
  }, [props.externalFilterRef]);

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => ({
      ...filter,
      interest: props.interestId,
    }),
    [props.interestId]
  );

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    if (!isString(rowId)) {
      showNotification.error(`Missing rowId for action ${actionKey}`);
      return;
    }

    const currentMiniPurchase = miniPurchaseRef.current;

    match(actionKey)
      .with('detail', () => {
        navigate(composePath(vehiclesRoutes.detail, {params: {id: rowId}}));
      })
      .with('removeFromOfferedVehicle', () => {
        if (
          !currentMiniPurchase ||
          !currentMiniPurchase.id ||
          isNil(currentMiniPurchase.offeredVehiclesIds?.find((id) => id === rowId))
        ) {
          showNotification.warning(i18n.t('entity.interest.actions.removeToOfferedVehicleWarning'));
          return;
        }
        const arg: UpdateMinipurchaseApiArg = {
          interestId: props.interestId,
          body: {
            // testDriveRequestedDate is response-only; the slot travels in testDriveScheduledAt.
            ...omit(['testDriveRequestedDate'], currentMiniPurchase),
            minipurchaseId: currentMiniPurchase.id,
            testDriveScheduledAt: toTestDriveSlotRequestBody(currentMiniPurchase),
            offeredVehiclesIds: currentMiniPurchase.offeredVehiclesIds?.filter(
              (id: string) => id !== rowId
            ),
          },
        };
        updateMinipurchase(arg)
          .unwrap()
          .then(() =>
            showNotification.info(i18n.t('entity.interest.actions.removeToOfferedVehicle'))
          )
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .with('addToOfferedVehicle', () => {
        if (!currentMiniPurchase || !currentMiniPurchase?.id) {
          const arg: CreateMinipurchaseApiArg = {
            body: {
              interestId: props.interestId,
              offeredVehiclesIds: [rowId],
            },
          };
          createMinipurchase(arg)
            .unwrap()
            .then(() => {
              showNotification.info(i18n.t('entity.interest.actions.addToOfferedVehicle'));
              refreshData();
            })
            .catch(handleApiError);
        } else {
          const vehicleId = rowId;

          addMinipurchaseOfferedVehicle({
            minipurchaseId: currentMiniPurchase.id,
            addMinipurchaseOfferedVehicleRequestBody: {
              vehicleId,
            },
          })
            .unwrap()
            .then(() => {
              showNotification.info(i18n.t('entity.interest.actions.addToOfferedVehicle'));
              refreshData();
            })
            .catch(handleApiError);
        }
      })
      .otherwise(() => {
        showNotification.warning(`Action callback was not specified for action ${actionKey}`);
      });
  };

  return (
    <DmsDataGrid
      maximization={props.maximization}
      data-testid={testIds.interest.detail('selling')}
      gridCode={OFFERABLE_VEHICLES_GRID_CODE}
      presetInstanceId={`interest-vehicle-list-${props.interestId}`}
      filterComponent={(params) => (
        <ExternalFilterConnector
          ref={props.externalFilterRef}
          onDataGridFilterChange={params.onChange}
        />
      )}
      actionCallback={actionCallback}
      externalFilterId={EXTERNAL_FILTER_ID}
      queryModifier={queryModifier}
      onExternalFilterChange={handleExternalFilterReady}
    />
  );
}
